import React, { useEffect, useRef, useState } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { createPortal } from "react-dom";

const NmPendingHelpDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((s) => !s);
  const drawerContentRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (drawerContentRef.current && !drawerContentRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const drawer = (
    <div className="drawer-portal">
      <Drawer
        open={isOpen}
        onClose={toggle}
        direction="right"
        size={500}
        duration={400}
        overlayOpacity={0.5}
        overlayColor="#000"
        customIdSuffix="help-guide-nm-pending"
        className="help-guide-drawer"
        style={{ top: "73px", height: "calc(100vh - 73px)", transition: "all 0.4s ease-in-out" }}
      >
        <div className="drawer-content" ref={drawerContentRef}>
          <div className="drawer-header">
            <div className="header-title">
              <span className="emoji">🟡</span>
              <span className="title">Near Miss – Pending Tab Help</span>
            </div>
            <button className="close-button" onClick={toggle} aria-label="Close help panel">
              &times;
            </button>
          </div>

          <div className="drawer-body">
  <p>
    The In-progress Near Miss (NM) Detail Page allows you to manage Near Miss records where the
    investigation has started. This page provides full visibility into investigation progress, analysis
    steps, and CAPA management.
  </p>

  <h5 className="section-title">Record Overview</h5>
  <p>At the top of the page, you will see:</p>
  <ul className="steps-list">
    <li>• Record Number – e.g., 940</li>
    <li>• NM Type – UA / UC (Unsafe Act / Unsafe Condition)</li>
    <li>• Activity Name – e.g., Bar Bending</li>
    <li>• NM Reported Month – e.g., July</li>
  </ul>
  <p>Below this, the timeline displays:</p>
  <ul className="steps-list">
    <li>• Reported details</li>
    <li>• Incident date</li>
    <li>• Involved person names</li>
    <li>• Investigation team members</li>
    <li>• Discussion comments (if any)</li>
  </ul>

  <h5 className="section-title">Reported Details Section</h5>
  <ul className="steps-list">
    <li>• Evidence – uploaded images.</li>
    <li>• Incident Description – details of the NM.</li>
    <li>• Hazard Type – hazard classification.</li>
    <li>• Risk Level – risk severity rating.</li>
  </ul>

  <h5 className="section-title">Activity Information Section</h5>
  <ul className="steps-list">
    <li>• Service and its linked Plant/Project/Client.</li>
    <li>• Department/Contractor/Location associated with the NM.</li>
  </ul>

  <h5 className="section-title">Incident Details Section</h5>
  <ul className="steps-list">
    <li>• Incident Date – when the NM occurred.</li>
    <li>• Incident Time – exact time of occurrence.</li>
    <li>• Involved Person Details – individuals involved in the NM.</li>
  </ul>

  <h5 className="section-title">NM Investigation Start Detail</h5>
  <ul className="steps-list">
    <li>• Shows the date the investigation officially started.</li>
    <li>
      • This is when the record moved from <b>Pending → In-progress</b> after assigning the investigation
      team.
    </li>
  </ul>

  <h5 className="section-title">NM Investigation Target & Team Details</h5>
  <ul className="steps-list">
    <li>
      • Displays the investigation target date – the deadline to complete investigation and CAPA
      recommendation.
    </li>
    <li>• Lists investigation team members.</li>
    <li>• Crown icon identifies the Team Lead.</li>
  </ul>

  <h5 className="section-title">NM Investigation Analysis – Three-Step Process</h5>

  <b>Step 1 – Incident Analysis Question (5Y Analysis)</b>
  <ul className="steps-list">
    <li>• Team can conduct a 5-Why analysis to trace the root causes of the NM.</li>
    <li>
      • To add: Click the plus icon → A pop-up appears → Enter the question and response → Click Done.
    </li>
    <li>• Documents related to the NM investigation can also be uploaded here.</li>
  </ul>

  <b>Step 2 – Root Cause Analysis</b>
  <ul className="steps-list">
    <li>• Investigation team can identify Contributing, Immediate, and Root Causes.</li>
    <li>
      • To add: Click on the respective factor name → A pop-up appears → Select the factors → Click
      Submit.
    </li>
    <li>• Selected root causes will automatically populate the Fishbone Diagram for visualization.</li>
  </ul>

  <b>Accident Costing (Optional Section)</b>
  <ul className="steps-list">
    <li>• If enabled in admin configuration, a costing section will appear.</li>
    <li>• Investigation team can add NM-related costs under various heads.</li>
    <li>• If not enabled, this section will not be visible.</li>
  </ul>

  <b>Step 3 – CAPA (Corrective & Preventive Action)</b>
  <ul className="steps-list">
    <li>
      • To propose CAPA: Click the plus icon → A pop-up appears → Fill required fields (Category, CAPA
      Type, Action, Assigned User, Due Date, Priority).
    </li>
    <li>• All team members can propose CAPAs.</li>
    <li>
      • Team Lead must review and approve CAPAs → Approve button → Confirmation pop-up → If approved,
      CAPA moves to the Final CAPA list.
    </li>
  </ul>

  <b>Final CAPA Tab</b>
  <ul className="steps-list">
    <li>• Team Lead has the exclusive option to Freeze All.</li>
    <li>
      • Clicking Freeze All finalizes the investigation → Confirmation pop-up → Select Yes → Record
      moves to Completed tab.
    </li>
    <li>
      • Final CAPAs shift to the CAPA In-progress tab for implementation → Assigned users are notified
      via email.
    </li>
  </ul>

  <h5 className="section-title">Discussion Section</h5>
  <p>A chat-based collaboration area where investigation team members can:</p>
  <ul className="steps-list">
    <li>• Exchange ideas and findings.</li>
    <li>• Discuss preventive measures.</li>
    <li>• Ensure the NM does not recur for the related activity.</li>
  </ul>
  <p>This collaborative approach keeps the team aligned and focused.</p>

  <h5 className="section-title">UAUC Detail Analysis (Right-hand Panel)</h5>
  <ul className="steps-list">
    <li>• IRA Analysis – accident statistics.</li>
    <li>• Near Miss Analysis – trends in reporting.</li>
    <li>• UA vs UC Analysis – unsafe act vs unsafe condition ratio.</li>
    <li>• Risk-Based Analysis – distribution of risk levels.</li>
    <li>• Good Act & TBT Count – safety-positive behavior and toolbox talks.</li>
    <li>• Internal & External Training Count – conducted trainings.</li>
    <li>• Employee & Contractor Training Count – training coverage.</li>
  </ul>
  <p>
    This data supports development of strategic, evidence-based interventions to reduce future UAUC
    incidents.
  </p>

  <div className="alert alert-info">
    <b>✅ Note:</b> The investigation must progress systematically through <b>Analysis → Root Causes →
    CAPA → Freeze</b> to ensure completeness and compliance.
  </div>
</div>
        </div>
      </Drawer>
    </div>
  );

  return (
    <>
      <div
        className="toggle-help-btn"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle();
        }}
        style={{ fontSize: "16px", cursor: "pointer" }}
      >
        Page Help
        <img src="/images/formkit_help.svg" alt="" />
      </div>
      {createPortal(drawer, document.body)}
    </>
  );
};

export default NmPendingHelpDrawer;
